import api from "@/config/api";
import { Report } from "@/types/report";
import { Params } from "@/types/utils";
import { log } from "@/utils/logger";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import useFileViewer, { FileDescriptor } from "@/components/common/modals/file-viewer/context";
import { getDownloadURL, getStorage, ref } from "firebase/storage";

const getAttachmentExtension = (attachment: Report["attachments"][number]) => {
    const mimeType = attachment.mimeType || "";
    const mimeExtension = mimeType.split("/")[1];
    if (mimeExtension && mimeExtension !== "octet-stream") return mimeExtension;
    const nameExtension = attachment.name.split(".").pop();
    return nameExtension || "bin";
};

const getSignedReportDownloadUrl = async (
    oid: string,
    attachment: Report["attachments"][number],
): Promise<string> => {
    const extension = getAttachmentExtension(attachment);
    const path = `organizations/${oid}/${attachment.md5}.${extension}`;
    const storage = getStorage();
    const storageRef = ref(storage, path);
    return getDownloadURL(storageRef);
};

const useUploadSignedReport = () => {
    const [uploading, setUploading] = useState(false);
    const { oid, sid, rid } = useParams<Params>();
    const queryClient = useQueryClient();

    const upload = async (file: File) => {
        if (!oid || !sid || !rid) return;
        try {
            setUploading(true);
            const formData = new FormData();
            formData.append("attachmentType", "SignedReport");
            formData.append("file", file);

            const data = await api.post<{ attachment: Report["attachments"][number] }>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}/attachments`,
                { data: formData },
            );

            queryClient.setQueryData<Report>(["report", rid], current => {
                if (!current) return current;
                return {
                    ...current,
                    attachments: [...current.attachments, data.attachment],
                };
            });
            queryClient.invalidateQueries({ queryKey: ["report", rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });
        } catch (e) {
            log.error(e);
        } finally {
            setUploading(false);
        }
    };

    return { uploading, upload };
};

const useDeleteSignedReportAttachment = () => {
    const [deleting, setDeleting] = useState<Record<string, boolean>>({});
    const { oid, sid, rid } = useParams<Params>();
    const queryClient = useQueryClient();

    const remove = async (md5: string) => {
        if (!oid || !sid || !rid) return;
        try {
            setDeleting(prev => ({ ...prev, [md5]: true }));
            await api.delete(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}/attachments/${md5}`,
            );

            queryClient.setQueryData<Report>(["report", rid], current => {
                if (!current) return current;
                return {
                    ...current,
                    attachments: current.attachments.filter(a => a.md5 !== md5),
                };
            });
            queryClient.invalidateQueries({ queryKey: ["report", rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });
        } catch (e) {
            log.error(e);
        } finally {
            setDeleting(prev => ({ ...prev, [md5]: false }));
        }
    };

    return { deleting, remove };
};

const usePreviewSignedReportAttachment = () => {
    const [previewing, setPreviewing] = useState<Record<string, boolean>>({});
    const { oid, sid, rid } = useParams<Params>();
    const open = useFileViewer(state => state.open);

    const preview = async (attachment: Report["attachments"][number]) => {
        if (!oid || !sid || !rid) return;
        try {
            setPreviewing(prev => ({ ...prev, [attachment.md5]: true }));
            const url = await getSignedReportDownloadUrl(oid, attachment);
            const file: FileDescriptor = {
                url,
                filename: attachment.name,
                mimeType: attachment.mimeType,
            };
            open(file);
        } catch (e) {
            log.error(e);
        } finally {
            setPreviewing(prev => ({ ...prev, [attachment.md5]: false }));
        }
    };

    return { previewing, preview };
};

export { useUploadSignedReport, useDeleteSignedReportAttachment, usePreviewSignedReportAttachment };
